import { useState } from 'react';
import { Check, Copy, RotateCcw, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Tooltip } from '@/shared/ui';
import styles from './AcoesBalaoMia.module.css';

type Props = {
  texto: string;
  /** Sem id (fala que nao foi gravada), curtir some. */
  idMensagem: number | null;
  nota: -1 | 1 | null;
  onAvaliar: (nota: -1 | 0 | 1, motivo?: string) => Promise<void>;
  /** So na ultima fala da Mia, sem proposta aberta e com o painel livre. */
  onRegenerar?: () => void;
};

const MOTIVOS = ['Resposta errada', 'Não entendeu o pedido', 'Faltou informação'];

/** Copiar, curtir e descurtir, no hover do balao da Mia (sempre visivel no toque). */
export function AcoesBalaoMia({ texto, idMensagem, nota, onAvaliar, onRegenerar }: Props) {
  const [copiado, setCopiado] = useState(false);
  const [pedindoMotivo, setPedindoMotivo] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1500);
    } catch {
      setCopiado(false);
    }
  }

  async function avaliar(nova: -1 | 0 | 1, comMotivo?: string) {
    setSalvando(true);
    try {
      await onAvaliar(nova, comMotivo);
    } finally {
      setSalvando(false);
    }
  }

  async function clicarDescurtir() {
    if (nota === -1) {
      setPedindoMotivo(false);
      await avaliar(0);
      return;
    }
    setPedindoMotivo(true);
  }

  /** A descurtida so grava quando a pessoa manda o motivo ou pula. */
  async function enviarMotivo(valor: string) {
    setPedindoMotivo(false);
    setMotivo('');
    await avaliar(-1, valor.trim() || undefined);
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.barra} role="toolbar" aria-label="Ações da resposta">
        <Tooltip content={copiado ? 'Copiado' : 'Copiar'} placement="top">
          <button type="button" className={styles.botao} aria-label="Copiar resposta" onClick={() => void copiar()}>
            {copiado ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </Tooltip>
        {idMensagem ? (
          <>
            <Tooltip content={nota === 1 ? 'Desfazer' : 'Boa resposta'} placement="top">
              <button
                type="button"
                className={nota === 1 ? `${styles.botao} ${styles.ativo}` : styles.botao}
                aria-label="Boa resposta"
                aria-pressed={nota === 1}
                disabled={salvando}
                onClick={() => {
                  setPedindoMotivo(false);
                  void avaliar(nota === 1 ? 0 : 1);
                }}
              >
                <ThumbsUp size={14} />
              </button>
            </Tooltip>
            <Tooltip content={nota === -1 ? 'Desfazer' : 'Resposta ruim'} placement="top">
              <button
                type="button"
                className={nota === -1 ? `${styles.botao} ${styles.ativo}` : styles.botao}
                aria-label="Resposta ruim"
                aria-pressed={nota === -1}
                disabled={salvando}
                onClick={() => void clicarDescurtir()}
              >
                <ThumbsDown size={14} />
              </button>
            </Tooltip>
          </>
        ) : null}
        {onRegenerar ? (
          <Tooltip content="Refazer resposta" placement="top">
            <button type="button" className={styles.botao} aria-label="Refazer resposta" onClick={onRegenerar}>
              <RotateCcw size={14} />
            </button>
          </Tooltip>
        ) : null}
      </div>
      {pedindoMotivo ? (
        <form
          className={styles.motivo}
          aria-label="O que faltou?"
          onSubmit={(event) => {
            event.preventDefault();
            void enviarMotivo(motivo);
          }}
        >
          <p>O que faltou? Isso ajuda a Mia a melhorar.</p>
          <div className={styles.chips}>
            {MOTIVOS.map((item) => (
              <button key={item} type="button" className={styles.chip} onClick={() => void enviarMotivo(item)}>
                {item}
              </button>
            ))}
          </div>
          <div className={styles.linhaMotivo}>
            <input
              className={styles.campo}
              value={motivo}
              maxLength={500}
              aria-label="Motivo"
              placeholder="Conte em poucas palavras"
              onChange={(event) => setMotivo(event.target.value)}
            />
            <button type="submit" className={styles.enviar} disabled={!motivo.trim()}>
              Enviar
            </button>
            <button type="button" className={styles.pular} onClick={() => void enviarMotivo('')}>
              Pular
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
